import { ListItem, ListItemText, Typography } from "@mui/material";
import React, { FC, memo } from "react";
import { FreightQuote } from "../GlobalTypes";
import formatIntWithCommas from "../util/formatIntWithCommas";
import priceWithZeros from "../util/priceWithZeros";
import AdminFactorSelect from "./AdminFactorSelect";
import { CustomCladColorInput } from "./CustomCladColorInput/CustomCladColorInput";
import formatDoubleWithCommas from "../util/formatDoubleWithCommas";
import { FreightQuotePriceInput } from "./FreightQuotePriceInput/FreightQuotePriceInput";

function getTotal(
  text: string,
  suggRetailPrice: string,
  delivery: number,
  customClad: number,
  splitFinish: number,
  dealerTax: number | null,
  grandTotal: string,
  adminCharge: number | null,
  freightCrate: number | null,
  freightRate: number | null,
  isCustomCladColorPriceUpdateRequired: boolean,
  isFreightQuoteUpdateRequired: boolean,
  canEditCustomCladColorOrFreightQuotePrice: boolean,
) {
  if (text === "Total" && suggRetailPrice) {
    return `$${suggRetailPrice}`;
  }

  if (text === "Crate: ") {
    return `$${freightCrate ? formatIntWithCommas(priceWithZeros(freightCrate)) : 0}`;
  }

  if (text === "Freight: ") {
    if (
      isFreightQuoteUpdateRequired &&
      !canEditCustomCladColorOrFreightQuotePrice
    ) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <Typography
            variant="body2"
            style={{
              color: "red",
              textAlign: "end",
              fontSize: "13px",
            }}
          >
            Contact office for new freight estimate
          </Typography>
        </div>
      );
    } else if (freightRate !== 0) {
      return `$${formatDoubleWithCommas(freightRate ?? 0)}`;
    }
  }

  if (text === "Delivery Charge: ") {
    return `$${delivery ? `${delivery}.00` : 0}`;
  }

  if (text === "Custom Clad Color: ") {
    if (
      isCustomCladColorPriceUpdateRequired &&
      !canEditCustomCladColorOrFreightQuotePrice
    ) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <Typography
            variant="body2"
            style={{
              color: "red",
              textAlign: "end",
              fontSize: "13px",
            }}
          >
            Contact office for new custom clad color estimate
          </Typography>
        </div>
      );
    } else if (customClad !== 0) {
      return `$${formatDoubleWithCommas(customClad)}`;
    }
  }

  if (text === "Split Finish: " && splitFinish !== 0) {
    return `$${formatIntWithCommas(splitFinish)}.00`;
  }

  if (text === "Sales Tax: " && dealerTax) {
    return `${dealerTax}%`;
  }

  if (text === "Dealer Net Cost: " && grandTotal) {
    return `$${grandTotal}`;
  }

  if (adminCharge) {
    return `$${adminCharge}`;
  }

  return "";
}

interface SidebarTotalsProps {
  quoteId: string;
  text: string;
  factor: number;
  canEditFactor: boolean;
  canEditCustomCladColorAndFreightPrice: boolean;
  suggRetailPrice: string;
  grandTotal: string;
  handleAdminFactor: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  adminFactor: string;
  customClad: number;
  splitFinish: number;
  deliveryCharge: number;
  dealerTax: number;
  adminCharge: number;
  freightQuote: FreightQuote | {};
  customCladColorOverMaxApplies: boolean;
  customCladColorPriceUpdateRequired: boolean;
  freightQuoteError: boolean;
  freightQuotePriceUpdateRequired: boolean;
}

const SidebarTotals: FC<SidebarTotalsProps> = ({
  quoteId,
  text,
  factor,
  canEditFactor,
  canEditCustomCladColorAndFreightPrice,
  suggRetailPrice,
  grandTotal,
  handleAdminFactor,
  adminFactor,
  customClad,
  deliveryCharge,
  dealerTax,
  adminCharge,
  freightQuote,
  splitFinish,
  customCladColorOverMaxApplies,
  customCladColorPriceUpdateRequired,
  freightQuoteError,
  freightQuotePriceUpdateRequired,
}) => {
  if (
    text === "Custom Clad Color: " &&
    customClad === 0 &&
    !customCladColorOverMaxApplies
  ) {
    return null;
  }

  if (text === "Split Finish: " && splitFinish === 0) {
    return null;
  }

  const isDealerNetCost = text === "Dealer Net Cost: ";

  const isCustomClad = text === "Custom Clad Color: ";

  const isFreightPrice = text === "Freight: ";

  const isCustomCladColorPriceUpdateRequired =
    isCustomClad && customCladColorPriceUpdateRequired;

  const isFreightQuotePriceUpdateRequired =
    isFreightPrice && freightQuotePriceUpdateRequired;

  const doesCustomCladColorOverMaxApply =
    isCustomClad && customCladColorOverMaxApplies;

  const freightQuoteErrorExists = isFreightPrice && freightQuoteError;

  const showContactOfficeMessage =
    (isCustomCladColorPriceUpdateRequired ||
      isFreightQuotePriceUpdateRequired) &&
    !canEditCustomCladColorAndFreightPrice;

  return (
    <ListItem style={{ paddingBottom: showContactOfficeMessage ? 0 : "" }}>
      <ListItemText
        style={{
          position: "relative",
          borderTop: isDealerNetCost ? "1px solid black" : "",
          height: isDealerNetCost ? "" : showContactOfficeMessage ? "auto" : 10,
          padding: 0,
        }}
        primary={
          <span
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 4,
            }}
          >
            <Typography
              component="span"
              variant="body2"
              color="textPrimary"
              style={{
                flexShrink: 0,
                fontWeight: isDealerNetCost ? "bold" : "",
              }}
            >
              {text === "Total" && canEditFactor ? (
                <>
                  {text} (Factor
                  <AdminFactorSelect
                    handleChange={handleAdminFactor}
                    adminFactor={adminFactor}
                  />
                  ) :
                </>
              ) : text === "Total" ? (
                `Total (Factor ${factor}):`
              ) : (
                text
              )}
            </Typography>

            <Typography
              style={{
                position: showContactOfficeMessage ? "static" : "absolute",
                right: showContactOfficeMessage ? undefined : 0,
                textAlign:
                  isCustomCladColorPriceUpdateRequired ||
                  isFreightQuotePriceUpdateRequired
                    ? "end"
                    : undefined,
                fontWeight: isDealerNetCost ? "bold" : "",
                minWidth: 0,
                width: showContactOfficeMessage
                  ? "auto"
                  : isCustomCladColorPriceUpdateRequired ||
                      isFreightQuotePriceUpdateRequired
                    ? "145px"
                    : "auto",
              }}
              component="span"
              variant="body2"
              color={
                isCustomCladColorPriceUpdateRequired ||
                isFreightQuotePriceUpdateRequired
                  ? "red"
                  : "textPrimary"
              }
              fontSize={
                isCustomCladColorPriceUpdateRequired ||
                isFreightQuotePriceUpdateRequired
                  ? "13px"
                  : ""
              }
            >
              {doesCustomCladColorOverMaxApply && canEditCustomCladColorAndFreightPrice ? (
                <CustomCladColorInput
                  quoteId={quoteId}
                  customCladColorPriceUpdateRequired={
                    isCustomCladColorPriceUpdateRequired
                  }
                  initialCustomCladColorPrice={customClad}
                />
              ) : freightQuoteErrorExists && canEditCustomCladColorAndFreightPrice? (
                <FreightQuotePriceInput
                  quoteId={quoteId}
                  initialFreightQuotePrice={
                    "rateQuoteCharge" in freightQuote
                      ? freightQuote.rateQuoteCharge
                      : null
                  }
                  initialFreightQuotePriceUpdateRequired={
                    freightQuotePriceUpdateRequired
                  }
                />
              ) : (
                getTotal(
                  text,
                  suggRetailPrice,
                  deliveryCharge,
                  customClad,
                  splitFinish,
                  dealerTax || null,
                  grandTotal,
                  adminCharge || null,
                  "crateCharge" in freightQuote
                    ? freightQuote.crateCharge
                    : null,
                  "rateQuoteCharge" in freightQuote
                    ? freightQuote.rateQuoteCharge
                    : null,
                  isCustomCladColorPriceUpdateRequired,
                  isFreightQuotePriceUpdateRequired,
                  canEditCustomCladColorAndFreightPrice,
                )
              )}
            </Typography>
          </span>
        }
      />
    </ListItem>
  );
};

export default memo(SidebarTotals);
